
import { supabase } from '@/integrations/supabase/client';
import { getCategoryByName, createCategory } from './categoryHelpers';

// Create a new product with optional category
export const createProduct = async (productName: string, categoryName?: string) => {
  // First check if category exists and get its ID
  let categoryId = null;
  if (categoryName && categoryName !== 'none') {
    const { data: categoryData, error: categoryError } = await supabase
      .from('categories')
      .select('id')
      .eq('name', categoryName)
      .single();
    
    if (!categoryError && categoryData) {
      categoryId = categoryData.id;
    }
  }
  
  // Create the product with the category ID if available
  const { data, error } = await supabase
    .from('products')
    .insert({ 
      name: productName,
      category_id: categoryId
    })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Create product with more details
export const createDetailedProduct = async (product: {
  name: string;
  category?: string;
  description?: string;
  unit_of_measure?: string;
  product_id?: string;
}) => {
  // First check if category exists and get its ID
  let categoryId = null;
  if (product.category && product.category !== 'none') {
    const categoryData = await getCategoryByName(product.category);
    
    if (categoryData) {
      categoryId = categoryData.id;
    } else {
      // Create the category if it doesn't exist
      try {
        const newCategory = await createCategory(product.category);
        if (newCategory) {
          categoryId = newCategory.id;
        }
      } catch (err) {
        console.error("Error creating category:", err);
      }
    }
  }
  
  // Create the product with all details
  const { data, error } = await supabase
    .from('products')
    .insert({ 
      name: product.name,
      category_id: categoryId,
      description: product.description || null,
      unit_of_measure: product.unit_of_measure || 'kg'
    })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Update an existing product
export const updateProduct = async (product: {
  id: string;
  name: string;
  category?: string;
  description?: string | null;
  unit_of_measure?: string;
}) => {
  // First check if category exists and get its ID
  let categoryId = null;
  if (product.category && product.category !== 'none') {
    const categoryData = await getCategoryByName(product.category);
    
    if (categoryData) {
      categoryId = categoryData.id;
    } else {
      // Create the category if it doesn't exist
      try {
        const newCategory = await createCategory(product.category);
        if (newCategory) {
          categoryId = newCategory.id;
        }
      } catch (err) {
        console.error("Error creating category:", err);
      }
    }
  }
  
  // Update the product with all details
  const { data, error } = await supabase
    .from('products')
    .update({ 
      name: product.name,
      category_id: categoryId,
      description: product.description || null,
      unit_of_measure: product.unit_of_measure || 'kg'
    })
    .eq('id', product.id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Delete a product
export const deleteProduct = async (productName: string) => {
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('name', productName);
  
  if (error) throw error;
  return true;
};
